import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { AddressModel } from '@data-layer/models/common';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';
import { enumShippingRole } from '@data-layer/enums/user';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';

@Component({
  selector: 'lcl-entity-edit-sidebar',
  templateUrl: './lcl-entity-edit.component.html',
  styleUrls: ['./lcl-entity-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclEntityEditComponent implements OnInit, OnDestroy {
  @Input() public entityCode: enumEntityModelCode;
  @Input() public entityName: string;
  @Input() public entity: EntityModel = new EntityModel();
  @Input() public isUpdateIncoterms: boolean;
  @Input() public isLCLForeign: boolean;
  @Input() public shippingRole: enumShippingRole;
  @Input() public incotermsType: enumIncotermsType;
  @Output() public dataSaved: EventEmitter<EntityModel> = new EventEmitter<EntityModel>();
  @Output() public incotermsUpdated: EventEmitter<enumIncotermsType> = new EventEmitter<enumIncotermsType>();
  @Output() public dataCleared: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public formGroup: UntypedFormGroup;
  public incotermsControl: UntypedFormControl;
  public code: enumEntityModelCode;
  public isShowMessage: boolean = false;
  public isFormCleared: boolean; // used to remove entity

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private fh: FormHelper) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createLCLEntityModelFormGroup(this.entityCode, false);
    this.entity = { ...this.entity, entityCode: this.entityCode, address: this.entity?.address || new AddressModel() };

    this.incotermsControl = new UntypedFormControl(this.incotermsType);
    this.setIncoterms(this.incotermsType);

    this.formGroup.patchValue(this.entity);
    this.setFormValidators();

    this.formGroup.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.setFormValidators();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public saveData(isNeedCheck: boolean = true) {
    this.isShowMessage = false;
    this.setFormValidators();
    const invalidFields = this.fh.validateAllFields(this.formGroup);
    if (isNeedCheck && Object.keys(invalidFields).length > 0) {
      this.openModal(invalidFields);
      return;
    }

    // we need hardcode entityCode & id due to form clear button clearing all values
    if (this.entityCode === enumEntityModelCode.NOTIFY_PARTY && this.isFormCleared) {
      this.dataCleared.emit(this.entity?.id);
    } else {
      this.dataSaved.emit({ ...this.formGroup.value, entityCode: this.entityCode, id: this.entity?.id });
    }
  }

  public setIncoterms(state: enumIncotermsType): void {
    this.incotermsControl.setValue(state, { emitEvent: false });
    this.incotermsUpdated.emit(state);
  }

  private openModal(data) {
    this.missingFieldModal.openCustomModal(data);
  }

  private setFormValidators(): void {
    // Address parts are mandatory except for State and Postal Code for non-US countries
    this.formGroup.get('address').get('state').clearValidators();
    this.formGroup.get('address').get('postalCode').clearValidators();

    if (this.formGroup.get('address').get('country').value?.code === USA_COUNTRY_CODE) {
      this.formGroup.get('address').get('state').setValidators(Validators.required);
      this.formGroup.get('address').get('postalCode').setValidators(Validators.required);
    } else {
      if (
        this.formGroup.get('address').get('country').value?.code !== CANADA_COUNTRY_CODE &&
        this.formGroup.get('address').get('country').value?.code !== MEXICO_COUNTRY_CODE
      ) {
        this.formGroup.get('address').get('state').setValue(null, { emitEvent: false });
      }
      this.formGroup.get('address').get('state').setErrors(null);
      this.formGroup.get('address').get('postalCode').setErrors(null);
    }
  }
}
